import { Component, OnInit, output } from "@angular/core";
import { KENDO_ICONS } from "@progress/kendo-angular-icons";
import { KENDO_INPUTS } from "@progress/kendo-angular-inputs";
import { KENDO_LABELS } from "@progress/kendo-angular-label";
import { KENDO_BUTTONS } from "@progress/kendo-angular-buttons";
import { iconListData } from "./icon-list";
import { IconData, CategoryGroup, scoreIcon } from "./icon-utils";

@Component({
  selector: "app-icon-list",
  imports: [KENDO_ICONS, KENDO_INPUTS, KENDO_LABELS, KENDO_BUTTONS],
  styleUrl: "./demo-styles.css",
  template: `
    <div class="category-filters">
      <button
        kendoButton
        size="small"
        [themeColor]="selectedCategory === '' ? 'primary' : 'base'"
        (click)="selectCategory('')"
      >
        All Categories ({{ getFilteredTotalCount() }})
      </button>
      @for (category of availableCategories; track category.name) {
        <button
          kendoButton
          size="small"
          [themeColor]="selectedCategory === category.name ? 'primary' : 'base'"
          (click)="selectCategory(category.name)"
        >
          {{ category.name }} ({{ getFilteredCategoryCount(category.name) }})
        </button>
      }
    </div>

    @for (category of filteredIcons; track category.name) {
      <h2 class="header" [attr.id]="'cat-' + category.name">
        {{ category.name }}
      </h2>
      <ul
        class="icons-wrapper"
        role="list"
        [attr.aria-labelledby]="'cat-' + category.name"
      >
        @for (icon of category.icons; track icon.id) {
          <li
            class="icon-item"
            tabindex="0"
            role="listitem"
            [attr.aria-label]="icon.name"
          >
            <button
              kendoButton
              class="k-button-none"
              (click)="clickButton(icon)"
            >
              <span
                class="k-icon k-font-icon {{ icon.className }}"
                aria-hidden="true"
              ></span>
            </button>
          </li>
        }
      </ul>
    }
  `,
})
export class IconListComponent implements OnInit {
  public searchTerm = "";
  public filteredIcons: CategoryGroup[] = [];
  public allIcons: CategoryGroup[] = [];
  public searchFilteredIcons: CategoryGroup[] = [];
  public isLoading = true;
  public usingFallback = false;
  public selectedCategory = "";
  public availableCategories: { name: string; count: number }[] = [];

  public iconClick = output<IconData>();

  ngOnInit(): void {
    this.loadIcons();
  }

  clickButton(icon: IconData) {
    this.iconClick.emit(icon);
  }

  private async loadIcons(): Promise<void> {
    this.loadFallbackIcons();
    /*try {
      const response = await fetch(
        "https://unpkg.com/@progress/kendo-font-icons/dist/icons.json",
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const icons: IconData[] = await response.json();
      const excludedIconCodes = new Set(["e945", "e0dd"]);
      const filteredIcons = (icons || []).filter(
        (icon) => !excludedIconCodes.has(icon.unicode),
      );

      this.allIcons = this.groupIconsByCategory(filteredIcons);
      this.searchFilteredIcons = this.allIcons;
      this.filteredIcons = this.allIcons;
      this.updateAvailableCategories();
      this.usingFallback = false;
      this.isLoading = false;
    } catch {
      // } catch (error) {
      this.loadFallbackIcons();
    }*/
  }

  private loadFallbackIcons(): void {
    this.allIcons = this.groupIconsByCategory(iconListData);
    this.searchFilteredIcons = this.allIcons;
    this.filteredIcons = this.allIcons;
    this.updateAvailableCategories();
    this.usingFallback = true;
    this.isLoading = false;
  }

  private groupIconsByCategory(icons: IconData[]): CategoryGroup[] {
    const categoryMap = new Map<string, IconData[]>();

    icons.forEach((icon) => {
      icon.className = `k-i-${icon.name}`;
      icon.displayName = icon.name;

      icon.categories.forEach((category) => {
        if (!categoryMap.has(category)) {
          categoryMap.set(category, []);
        }
        const categoryIcons = categoryMap.get(category);
        if (categoryIcons) {
          categoryIcons.push(icon);
        }
      });
    });

    return Array.from(categoryMap.entries())
      .map(([categoryName, categoryIcons]) => ({
        name: this.capitalizeFirstLetter(categoryName),
        icons: categoryIcons.sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private capitalizeFirstLetter(string: string): string {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  public filterIcons(): void {
    const fullTerm = this.searchTerm.toLowerCase().trim();
    if (!fullTerm) {
      this.searchFilteredIcons = this.allIcons;
      this.filteredIcons = this.selectedCategory
        ? this.allIcons.filter((c) => c.name === this.selectedCategory)
        : this.allIcons;
      return;
    }
    const tokens = fullTerm.split(/\s+/);

    this.searchFilteredIcons = this.allIcons
      .map((category) => {
        const scored = category.icons
          .map((icon) => ({ icon, score: scoreIcon(icon, fullTerm, tokens) }))
          .filter((entry) => entry.score > 0)
          .sort((a, b) => b.score - a.score);
        return { name: category.name, icons: scored.map((s) => s.icon) };
      })
      .filter((category) => category.icons.length > 0);

    this.filteredIcons = this.selectedCategory
      ? this.searchFilteredIcons.filter((c) => c.name === this.selectedCategory)
      : this.searchFilteredIcons;
  }

  public selectCategory(categoryName: string): void {
    this.selectedCategory = categoryName;
    this.filterIcons();
  }

  public getFilteredTotalCount(): number {
    return this.searchFilteredIcons.reduce(
      (total, category) => total + category.icons.length,
      0,
    );
  }

  public getFilteredCategoryCount(categoryName: string): number {
    const category = this.searchFilteredIcons.find(
      (c) => c.name === categoryName,
    );
    return category ? category.icons.length : 0;
  }

  private updateAvailableCategories(): void {
    this.availableCategories = this.allIcons
      .map((category) => ({
        name: category.name,
        count: category.icons.length,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
}
